import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { externalLogAPI } from '../services/api';
import Icon from './Icon';

function prettyJSON(value) {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return 'N/A';
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return value;
    }
  }
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

function syntaxHighlight(json) {
  json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let cls = 'json-number';
      if (/^"/.test(match)) {
        cls = /:$/.test(match) ? 'json-key' : 'json-string';
      } else if (/true|false/.test(match)) {
        cls = 'json-boolean';
      } else if (/null/.test(match)) {
        cls = 'json-null';
      }
      return `<span class="${cls}">${match}</span>`;
    }
  );
}

function safeParse(value) {
  if (typeof value === 'string') {
    try { return JSON.parse(value); } catch { return null; }
  }
  if (value && typeof value === 'object') return value;
  return null;
}

function jsonDiff(req, res) {
  const a = safeParse(req);
  const b = safeParse(res);
  if (!a || !b || Array.isArray(a) || Array.isArray(b)) return null;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  const onlyReq = aKeys.filter(k => !(k in b));
  const onlyRes = bKeys.filter(k => !(k in a));
  const changed = aKeys.filter(k => k in b && JSON.stringify(a[k]) !== JSON.stringify(b[k]));
  return { onlyReq, onlyRes, changed };
}

function JsonBlock({ label, value }) {
  const text = prettyJSON(value);
  const isNA = text === 'N/A';
  const copy = () => {
    if (isNA) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied to clipboard`),
      () => toast.error('Failed to copy')
    );
  };
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', letterSpacing: '0.5px' }}>{label}</div>
        {!isNA && (
          <button className="btn btn-outline btn-xs" onClick={copy}>Copy</button>
        )}
      </div>
      {isNA ? (
        <div style={{ fontSize: '13px', color: '#94a3b8', padding: '8px 0' }}>N/A</div>
      ) : (
        <pre className="json-pre" dangerouslySetInnerHTML={{ __html: syntaxHighlight(text) }} />
      )}
    </div>
  );
}

function formatDuration(ms) {
  if (ms === null || ms === undefined) return '—';
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
  return `${ms}ms`;
}

function ExternalApiLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [diffMode, setDiffMode] = useState(false);
  const [filters, setFilters] = useState({
    endpoint: '',
    method: '',
    status: '',
    apiKey: '',
    startDate: '',
    endDate: '',
    search: '',
    referral: ''
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [preview, setPreview] = useState({ id: null, req: false, res: false });

  const togglePreview = (id) => {
    setPreview(p => {
      if (p.id === id && p.req && p.res) {
        return { id: null, req: false, res: false };
      }
      return { id, req: true, res: true };
    });
  };
  const [purgeDays, setPurgeDays] = useState(30);
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadLogs = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 50,
        endpoint: filters.endpoint,
        method: filters.method,
        status: filters.status,
        apiKey: filters.apiKey,
        startDate: filters.startDate,
        endDate: filters.endDate,
        search: filters.search,
        referral: filters.referral,
        sortBy,
        sortOrder
      };
      const response = await externalLogAPI.getLogs(params);
      setLogs(response.data.logs || []);
      setPagination(response.data.pagination || { page: 1, limit: 50, total: 0 });
    } catch (error) {
      toast.error('Failed to load external API logs');
    } finally {
      setLoading(false);
    }
  }, [filters, sortBy, sortOrder]);

  const loadStats = useCallback(async () => {
    try {
      const response = await externalLogAPI.getStats();
      setStats(response.data);
    } catch (error) {
      // Stats are secondary; don't block the page on failure
    }
  }, []);

  useEffect(() => {
    loadLogs(currentPage);
  }, [currentPage, loadLogs]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const applyFilters = () => {
    setCurrentPage(1);
    setSelectedIds(new Set());
    setPreview({ id: null, req: false, res: false });
    loadLogs(1);
  };

  const resetFilters = () => {
    setFilters({ endpoint: '', method: '', status: '', apiKey: '', startDate: '', endDate: '', search: '', referral: '' });
    setSortBy('created_at');
    setSortOrder('DESC');
    setCurrentPage(1);
    setSelectedIds(new Set());
    setPreview({ id: null, req: false, res: false });
  };

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'DESC' ? 'ASC' : 'DESC');
    } else {
      setSortBy(column);
      setSortOrder('DESC');
    }
  };

  const handleDelete = async (id) => {
    try {
      if (!window.confirm('Permanently delete this log entry?')) return;
      await externalLogAPI.delete(id);
      setLogs(prev => prev.filter(l => l.id !== id));
      toast.success('Log entry deleted permanently');
    } catch (e) {
      toast.error('Failed to delete log entry');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (prev.size === logs.length && logs.length > 0) return new Set();
      return new Set(logs.map(l => l.id));
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Permanently delete ${selectedIds.size} selected log entr${selectedIds.size === 1 ? 'y' : 'ies'}?`)) return;
    try {
      setBulkBusy(true);
      const ids = [...selectedIds];
      await externalLogAPI.bulkDelete(ids);
      setLogs(prev => prev.filter(l => !selectedIds.has(l.id)));
      toast.success(`Deleted ${ids.length} log entr${ids.length === 1 ? 'y' : 'ies'}`);
      setSelectedIds(new Set());
      setPreview({ id: null, req: false, res: false });
      loadStats();
    } catch (e) {
      toast.error('Failed to delete selected logs');
    } finally {
      setBulkBusy(false);
    }
  };

  const handlePurge = async () => {
    if (!window.confirm(`Purge all external API logs older than ${purgeDays} days? This cannot be undone.`)) return;
    try {
      setBulkBusy(true);
      const res = await externalLogAPI.purge(purgeDays);
      toast.success(res.data?.message || 'Logs purged');
      setCurrentPage(1);
      setSelectedIds(new Set());
      setPreview({ id: null, req: false, res: false });
      loadLogs(1);
      loadStats();
    } catch (e) {
      toast.error('Failed to purge logs');
    } finally {
      setBulkBusy(false);
    }
  };

  const exportCSV = () => {
    const csvContent = [
      'ID,Timestamp,Method,Endpoint,IP Address,API Key,Response Status,Duration(ms),User Agent',
      ...logs.map(log => [
        log.id,
        log.created_at,
        log.method,
        log.endpoint,
        log.ip_address,
        log.api_key_present ? 'Present' : 'Missing',
        log.response_status || '',
        log.duration_ms != null ? log.duration_ms : '',
        `"${(log.user_agent || '').replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `external_api_logs_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('External API logs exported successfully!');
  };

  const statusColor = (status) => {
    if (status === null || status === undefined) return '#94a3b8';
    if (status >= 500) return '#ef4444';
    if (status >= 400) return '#f59e0b';
    if (status >= 300) return '#8b5cf6';
    if (status >= 200) return '#10b981';
    return '#64748b';
  };

  const methodColor = (method) => {
    const colors = {
      GET: '#10b981',
      POST: '#3b82f6',
      PUT: '#f59e0b',
      PATCH: '#8b5cf6',
      DELETE: '#ef4444'
    };
    return colors[method] || '#64748b';
  };

  const totalPages = Math.ceil((pagination?.total || 0) / (pagination?.limit || 50));
  const allSelected = logs.length > 0 && selectedIds.size === logs.length;

  if (loading && logs.length === 0) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            <Icon name="database" />
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading External API Logs...</h3>
          <p style={{ color: '#94a3b8', marginTop: '8px' }}>Fetching ERP integration and external API call history</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">External API Logs</h1>
          <p className="page-subtitle">Monitor every incoming call from the ERP and external integrations</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-outline" onClick={exportCSV}>
            <Icon name="download" /> Export
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              setLoading(true);
              loadLogs(currentPage);
              loadStats();
              toast.success('External API logs refreshed!');
            }}
          >
            <Icon name="refresh" /> Refresh
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Calls</div>
            <div className="stat-card-icon"><Icon name="database" /></div>
          </div>
          <div className="stat-card-value">{stats?.totals?.total_calls || 0}</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            All recorded external requests
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Successful</div>
            <div className="stat-card-icon"><Icon name="check" /></div>
          </div>
          <div className="stat-card-value" style={{ color: '#10b981' }}>
            {stats?.totals?.success_count || 0}
          </div>
          <div className="stat-card-change positive">
            {stats?.totals?.total_calls ? Math.round((stats.totals.success_count / stats.totals.total_calls) * 100) : 0}% success rate
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Errors</div>
            <div className="stat-card-icon"><Icon name="alert" /></div>
          </div>
          <div className="stat-card-value" style={{ color: stats?.totals?.error_count ? '#ef4444' : undefined }}>
            {stats?.totals?.error_count || 0}
          </div>
          <div style={{ fontSize: '13px', color: stats?.totals?.error_count ? '#ef4444' : '#64748b', marginTop: '8px' }}>
            {stats?.totals?.error_count ? 'Requires attention' : 'No errors recorded'}
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Missing API Key</div>
            <div className="stat-card-icon"><Icon name="lock" /></div>
          </div>
          <div className="stat-card-value" style={{ color: stats?.totals?.api_key_missing ? '#f59e0b' : undefined }}>
            {stats?.totals?.api_key_missing || 0}
          </div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            Requests without a valid API key
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Avg Duration</div>
            <div className="stat-card-icon"><Icon name="clock" /></div>
          </div>
          <div className="stat-card-value">{formatDuration(stats?.totals?.avg_duration_ms || 0)}</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            Mean response time
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Slowest Call</div>
            <div className="stat-card-icon"><Icon name="trending-up" /></div>
          </div>
          <div className="stat-card-value">{formatDuration(stats?.totals?.max_duration_ms || 0)}</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            Worst response time
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Filters</h2>
            <p className="card-subtitle">Search and filter external API activity</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>Search</label>
            <input
              className="form-input"
              placeholder="Free-text: endpoint, IP, body, status..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
            />
          </div>
          <div>
            <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>Method</label>
            <select
              className="form-select"
              value={filters.method}
              onChange={(e) => setFilters({ ...filters, method: e.target.value })}
            >
              <option value="">All Methods</option>
              {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>Response Status</label>
            <select
              className="form-select"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All Statuses</option>
              {[200, 400, 401, 403, 404, 500].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>API Key</label>
            <select
              className="form-select"
              value={filters.apiKey}
              onChange={(e) => setFilters({ ...filters, apiKey: e.target.value })}
            >
              <option value="">All</option>
              <option value="present">Present</option>
              <option value="missing">Missing</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>Referral Code</label>
            <select
              className="form-select"
              value={filters.referral}
              onChange={(e) => setFilters({ ...filters, referral: e.target.value })}
            >
              <option value="">All</option>
              <option value="has">Has code</option>
              <option value="empty">Empty / missing</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>From Date</label>
            <input
              type="date"
              className="form-input"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            />
          </div>
          <div>
            <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>To Date</label>
            <input
              type="date"
              className="form-input"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '20px', justifyContent: 'flex-end' }}>
          <button className="btn btn-outline" onClick={resetFilters}>Reset Filters</button>
          <button className="btn btn-primary" onClick={applyFilters}>Apply Filters</button>
        </div>
      </div>

      {/* Top Endpoints */}
      {stats?.top_endpoints?.length > 0 && (
        <div className="card">
          <div className="card-header">
            <div>
              <h2 className="card-title">Top Endpoints</h2>
              <p className="card-subtitle">Most frequently called external API endpoints</p>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {stats.top_endpoints.map((ep, i) => (
              <span key={i} className="badge badge-info" style={{ padding: '8px 12px', fontSize: '13px' }}>
                {ep.method} {ep.endpoint} — {ep.count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', borderColor: 'var(--secondary-color)' }}>
          <div style={{ fontWeight: '600', color: 'var(--primary-color)' }}>
            {selectedIds.size} selected
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button className="btn btn-outline btn-sm" onClick={() => setSelectedIds(new Set())}>Clear</button>
            <button className="btn btn-danger btn-sm" onClick={handleBulkDelete} disabled={bulkBusy}>
              <Icon name="trash" /> Delete selected
            </button>
          </div>
        </div>
      )}

      {/* Logs Table */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">API Call History ({logs.length})</h2>
            <p className="card-subtitle">Every request made to the external endpoints</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: '#64748b' }}>Purge older than</span>
            <input
              type="number"
              min="1"
              className="form-input"
              style={{ width: '80px', padding: '6px 8px' }}
              value={purgeDays}
              onChange={(e) => setPurgeDays(Math.max(1, parseInt(e.target.value) || 1))}
            />
            <span style={{ fontSize: '13px', color: '#64748b' }}>days</span>
            <button className="btn btn-outline btn-sm" onClick={handlePurge} disabled={bulkBusy}>
              <Icon name="trash" /> Purge
            </button>
          </div>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '36px' }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} aria-label="Select all" />
                </th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('created_at')}>
                  # {sortBy === 'created_at' ? (sortOrder === 'DESC' ? '▼' : '▲') : ''}
                </th>
                <th>Method</th>
                <th>Endpoint</th>
                <th>Status</th>
                <th>IP Address</th>
                <th>API Key</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('duration_ms')}>
                  Duration {sortBy === 'duration_ms' ? (sortOrder === 'DESC' ? '▼' : '▲') : ''}
                </th>
                <th>Timestamp</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => setSelectedLog(log)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.has(log.id)} onChange={() => toggleSelect(log.id)} aria-label={`Select log ${log.id}`} />
                    </td>
                    <td style={{ fontWeight: '600', color: '#64748b' }}>
                      #{log.id}
                    </td>
                    <td>
                      <span className="badge" style={{ backgroundColor: `${methodColor(log.method)}20`, color: methodColor(log.method), border: `1px solid ${methodColor(log.method)}40`, fontWeight: '700' }}>
                        {log.method}
                      </span>
                    </td>
                    <td>
                      <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px', fontWeight: '500' }}>
                        {log.endpoint}
                      </div>
                    </td>
                    <td>
                      <span className="badge" style={{ backgroundColor: `${statusColor(log.response_status)}20`, color: statusColor(log.response_status), border: `1px solid ${statusColor(log.response_status)}40`, fontWeight: '700' }}>
                        {log.response_status ?? 'N/A'}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>{log.ip_address}</div>
                    </td>
                    <td>
                      {log.api_key_present ? (
                        <span className="badge badge-success" style={{ fontWeight: '700' }}><Icon name="check" /> Present</span>
                      ) : (
                        <span className="badge" style={{ backgroundColor: '#f59e0b20', color: '#f59e0b', border: '1px solid #f59e0b40', fontWeight: '700' }}><Icon name="lock" /> Missing</span>
                      )}
                    </td>
                    <td style={{ fontWeight: '600', color: log.duration_ms != null && log.duration_ms > 1000 ? '#ef4444' : '#475569' }}>
                      {formatDuration(log.duration_ms)}
                    </td>
                    <td>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{new Date(log.created_at).toLocaleString()}</div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                        <button
                          className="btn btn-outline btn-xs"
                          title="Request & response body preview"
                          onClick={(e) => { e.stopPropagation(); togglePreview(log.id); }}
                          style={preview.id === log.id && preview.req && preview.res ? { borderColor: 'var(--secondary-color)', color: 'var(--secondary-color)' } : undefined}
                        >
                          <Icon name="file-text" /> Body
                        </button>
                        <button className="btn-icon" title="View Details" onClick={(e) => { e.stopPropagation(); setSelectedLog(log); setDiffMode(false); }}><Icon name="eye" /></button>
                        <button className="btn-icon" title="Delete" onClick={(e) => { e.stopPropagation(); handleDelete(log.id); }}><Icon name="trash" /></button>
                      </div>
                    </td>
                  </tr>
                  {preview.id === log.id && (preview.req || preview.res) && (
                    <tr>
                      <td colSpan={10} style={{ background: 'var(--background-color, #f8fafc)', padding: '16px 24px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: (preview.req && preview.res) ? '1fr 1fr' : '1fr', gap: '20px' }}>
                          {preview.req && <JsonBlock label="REQUEST BODY (preview)" value={log.request_body} />}
                          {preview.res && <JsonBlock label="RESPONSE BODY (preview)" value={log.response_body} />}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}><Icon name="globe" /></div>
                    <h3 style={{ fontWeight: '600', marginBottom: '8px' }}>No External API Logs Found</h3>
                    <p style={{ fontSize: '14px' }}>
                      No external API calls match your filters. Calls to /api/v1/external/* are logged here.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ padding: '24px 32px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>
            Showing <span style={{ color: '#1e293b', fontWeight: '600' }}>{(((pagination?.page || 1) - 1) * (pagination?.limit || 50)) + 1}</span> to{' '}
            <span style={{ color: '#1e293b', fontWeight: '600' }}>{Math.min((pagination?.page || 1) * (pagination?.limit || 50), pagination?.total || 0)}</span> of{' '}
            <span style={{ color: '#1e293b', fontWeight: '600' }}>{pagination?.total || 0}</span> records
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || loading}
            >
              Previous
            </button>
            <div style={{ display: 'flex', gap: '4px' }}>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    className={`btn ${currentPage === pageNum ? 'btn-primary' : 'btn-outline'} btn-xs`}
                    onClick={() => setCurrentPage(pageNum)}
                    style={{ minWidth: '32px', height: '32px', padding: 0 }}
                    disabled={loading}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages || loading}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="modal-overlay" onClick={() => setSelectedLog(null)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0, fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span className="badge" style={{ backgroundColor: `${methodColor(selectedLog.method)}20`, color: methodColor(selectedLog.method), border: `1px solid ${methodColor(selectedLog.method)}40` }}>
                  {selectedLog.method}
                </span>
                {selectedLog.endpoint}
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  className={`btn btn-outline btn-sm ${diffMode ? 'btn-primary' : ''}`}
                  onClick={() => setDiffMode(prev => !prev)}
                >
                  <Icon name="clipboard" /> Diff
                </button>
                <button className="modal-close" onClick={() => setSelectedLog(null)}>✕</button>
              </div>
            </div>
            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>IP ADDRESS</div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>{selectedLog.ip_address || 'N/A'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>RESPONSE STATUS</div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: statusColor(selectedLog.response_status) }}>
                    {selectedLog.response_status ?? 'N/A'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>API KEY</div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>
                    {selectedLog.api_key_present ? <span style={{ color: '#10b981' }}>Present</span> : <span style={{ color: '#f59e0b' }}>Missing</span>}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>DURATION</div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>{formatDuration(selectedLog.duration_ms)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>TIMESTAMP</div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>{new Date(selectedLog.created_at).toLocaleString()}</div>
                </div>
              </div>

              {diffMode ? (
                <DiffView req={selectedLog.request_body} res={selectedLog.response_body} />
              ) : (
                <>
                  <JsonBlock label="REQUEST HEADERS" value={selectedLog.request_headers} />
                  <JsonBlock label="REQUEST QUERY" value={selectedLog.request_query} />
                  <JsonBlock label="REQUEST BODY" value={selectedLog.request_body} />
                  <JsonBlock label="RESPONSE BODY" value={selectedLog.response_body} />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DiffView({ req, res }) {
  const diff = jsonDiff(req, res);
  if (!diff) {
    return (
      <div style={{ fontSize: '13px', color: '#94a3b8', padding: '12px 0' }}>
        Diff is only available when both request and response bodies are JSON objects.
      </div>
    );
  }
  const { onlyReq, onlyRes, changed } = diff;
  const hasDiff = onlyReq.length || onlyRes.length || changed.length;
  return (
    <div>
      <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '8px' }}>
        DIFF — keys only in request, only in response, and changed values
      </div>
      {!hasDiff && (
        <div style={{ fontSize: '13px', color: '#10b981', fontWeight: '600', padding: '8px 0' }}>
          Request and response bodies are identical.
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
        <DiffColumn title="Only in Request" items={onlyReq} color="#f59e0b" empty="—" />
        <DiffColumn title="Only in Response" items={onlyRes} color="#3b82f6" empty="—" />
        <DiffColumn title="Changed Values" items={changed} color="#ef4444" empty="—" />
      </div>
    </div>
  );
}

function DiffColumn({ title, items, color, empty }) {
  return (
    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px', border: '1px solid #e2e8f0' }}>
      <div style={{ fontSize: '12px', fontWeight: '700', color, marginBottom: '8px' }}>{title}</div>
      {items.length === 0 ? (
        <div style={{ fontSize: '13px', color: '#94a3b8' }}>{empty}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {items.map(k => (
            <code key={k} style={{ fontSize: '12px', fontFamily: 'monospace', color: '#334155', background: '#fff', padding: '4px 8px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>{k}</code>
          ))}
        </div>
      )}
    </div>
  );
}

export default ExternalApiLogs;
